import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2 } from "lucide-react";
import Header from "../components/azhome/AzHeader";
import { useAuth } from "../context/AuthContext";
import "../auth.css";

const ForgotPassword = () => {
  const { forgotPassword, resetPassword } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // email | reset | done
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const sendCode = async () => {
    await forgotPassword(email);
    setStep("reset");
    setCooldown(60);
  };

  const onEmailSubmit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try { await sendCode(); } catch { setStep("reset"); setCooldown(60); } finally { setBusy(false); }
  };

  const onResend = async () => {
    if (cooldown > 0) return;
    setError("");
    try { await forgotPassword(email); setCooldown(60); }
    catch (err) { setError((typeof err.response?.data?.detail === "string" && err.response.data.detail) || t("auth.otpResendFailed")); }
  };

  const onResetSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError(t("auth.pwTooShort")); return; }
    if (password !== confirm) { setError(t("auth.pwMismatch")); return; }
    setBusy(true);
    try {
      await resetPassword(email, otp.trim(), password);
      setStep("done");
      setTimeout(() => navigate("/login"), 2600);
    } catch (err) {
      setError((typeof err.response?.data?.detail === "string" && err.response.data.detail) || t("auth.resetFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header />
      <main className="auth-page">
        <div className="auth-card" data-testid="forgot-password-page">
          {step === "email" && (
            <>
              <p className="overline">{t("auth.forgotOverline")}</p>
              <h1>{t("auth.forgotTitle")}</h1>
              <p className="auth-sub">{t("auth.forgotSubOtp")}</p>
              {error && <div className="auth-error" data-testid="forgot-error">{error}</div>}
              <form onSubmit={onEmailSubmit}>
                <div className="auth-field">
                  <label htmlFor="email">{t("auth.emailLabel")}</label>
                  <input id="email" type="email" required autoComplete="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="forgot-email" />
                </div>
                <button className="button blue auth-submit" type="submit" disabled={busy} data-testid="forgot-submit">
                  {busy ? t("auth.sending") : t("auth.sendResetCode")}
                </button>
              </form>
              <p className="auth-switch"><Link to="/login">{t("auth.backToLogin")}</Link></p>
            </>
          )}

          {step === "reset" && (
            <div data-testid="forgot-reset-step">
              <p className="overline">{t("auth.resetOverline")}</p>
              <h1>{t("auth.resetTitle")}</h1>
              <p className="auth-sub">{t("auth.resetSubOtp", { email })}</p>
              <div className="auth-error" style={{ background: "#FFF7ED", color: "#9A3412", border: "1px solid #FED7AA" }} data-testid="reset-withdraw-notice">
                {t("auth.resetWithdrawNotice")}
              </div>
              {error && <div className="auth-error" data-testid="reset-error">{error}</div>}
              <form onSubmit={onResetSubmit}>
                <div className="auth-field">
                  <label htmlFor="otp">{t("auth.otpCodeLabel")}</label>
                  <input id="otp" type="text" inputMode="numeric" maxLength={6} required autoFocus value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder={t("auth.otpPh")} data-testid="reset-otp-input" />
                </div>
                <div className="auth-field">
                  <label htmlFor="password">{t("auth.newPassword")}</label>
                  <input id="password" type="password" required autoComplete="new-password" value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder={t("auth.newPasswordPh")} data-testid="reset-password-input" />
                </div>
                <div className="auth-field">
                  <label htmlFor="confirm">{t("auth.confirmPassword")}</label>
                  <input id="confirm" type="password" required autoComplete="new-password" value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} placeholder={t("auth.confirmPasswordPh")} data-testid="reset-confirm-input" />
                </div>
                <button className="button blue auth-submit" type="submit" disabled={busy || otp.length !== 6} data-testid="reset-submit">
                  {busy ? t("auth.resetting") : t("auth.resetBtn")}
                </button>
              </form>
              <p className="auth-switch">
                <button type="button" onClick={onResend} disabled={cooldown > 0} data-testid="reset-otp-resend"
                  style={{ background: "none", border: "none", color: cooldown > 0 ? "var(--muted,#94a3b8)" : "var(--accent,#0B5CFF)", cursor: cooldown > 0 ? "default" : "pointer", fontWeight: 600 }}>
                  {cooldown > 0 ? t("auth.otpResendIn", { s: cooldown }) : t("auth.otpResend")}
                </button>
              </p>
            </div>
          )}

          {step === "done" && (
            <div style={{ textAlign: "center" }} data-testid="reset-success">
              <CheckCircle2 size={48} style={{ margin: "0 auto 14px", color: "#16b979" }} />
              <h1>{t("auth.resetDoneTitle")}</h1>
              <p className="auth-sub">{t("auth.resetDoneSub")}</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default ForgotPassword;
